import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import {
  readBoolean,
  readInputType,
  readNumber,
  readScriptTurns,
  readString,
  readStringAllowEmpty,
  readStringArray,
} from "./actionArgumentReaders"
import { readPersonaConfiguration } from "./personaActionConfig"

export function parseModifyActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only resource mutation actions.
  switch (action.kind) {
    case "update_agent": {
      const agent_id = readString(action.args, "agent_id")
      if (!agent_id) {
        return { ok: false, problems: ["missing agent_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_agent",
          args: {
            agent_id,
            display_name: readString(action.args, "display_name"),
            phone_number: readString(action.args, "phone_number"),
          },
        },
      }
    }
    case "delete_agent": {
      const agent_id = readString(action.args, "agent_id")
      if (!agent_id) {
        return { ok: false, problems: ["missing agent_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_agent", args: { agent_id } },
      }
    }
    case "update_persona": {
      const persona_id = readString(action.args, "persona_id")
      if (!persona_id) {
        return { ok: false, problems: ["missing persona_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_persona",
          args: {
            persona_id,
            name: readString(action.args, "name"),
            persona_prompt: readStringAllowEmpty(action.args, "persona_prompt"),
            voice_name: readString(action.args, "voice_name"),
            language_code: readString(action.args, "language_code"),
            ...readPersonaConfiguration(action.args),
          },
        },
      }
    }
    case "delete_persona": {
      const persona_id = readString(action.args, "persona_id")
      if (!persona_id) {
        return { ok: false, problems: ["missing persona_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_persona", args: { persona_id } },
      }
    }
    case "update_metric": {
      const metric_id = readString(action.args, "metric_id")
      if (!metric_id) {
        return { ok: false, problems: ["missing metric_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_metric",
          args: {
            metric_id,
            metric_name: readString(action.args, "metric_name"),
            description: readString(action.args, "description"),
            prompt: readString(action.args, "prompt"),
          },
        },
      }
    }
    case "delete_metric": {
      const metric_id = readString(action.args, "metric_id")
      if (!metric_id) {
        return { ok: false, problems: ["missing metric_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_metric", args: { metric_id } },
      }
    }
    case "update_dashboard": {
      const dashboard_id = readString(action.args, "dashboard_id")
      if (!dashboard_id) {
        return { ok: false, problems: ["missing dashboard_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_dashboard",
          args: {
            dashboard_id,
            display_name: readString(action.args, "display_name"),
            description: readString(action.args, "description"),
            is_favorite: readBoolean(action.args, "is_favorite"),
            is_default: readBoolean(action.args, "is_default"),
          },
        },
      }
    }
    case "delete_dashboard": {
      const dashboard_id = readString(action.args, "dashboard_id")
      if (!dashboard_id) {
        return { ok: false, problems: ["missing dashboard_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_dashboard", args: { dashboard_id } },
      }
    }
    case "update_report": {
      const report_id = readString(action.args, "report_id")
      if (!report_id) {
        return { ok: false, problems: ["missing report_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_report",
          args: {
            report_id,
            name: readString(action.args, "name"),
            run_ids: readStringArray(action.args, "run_ids"),
            compare_by: readString(action.args, "compare_by"),
            metadata_key: readString(action.args, "metadata_key"),
            permissions: readString(action.args, "permissions"),
          },
        },
      }
    }
    case "delete_report": {
      const report_id = readString(action.args, "report_id")
      if (!report_id) {
        return { ok: false, problems: ["missing report_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_report", args: { report_id } },
      }
    }
    case "update_monitor": {
      const monitor_id = readString(action.args, "monitor_id")
      if (!monitor_id) {
        return { ok: false, problems: ["missing monitor_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_monitor",
          args: {
            monitor_id,
            name: readString(action.args, "name"),
            threshold_float: readNumber(action.args, "threshold_float"),
            operator: readString(action.args, "operator"),
            scope: readString(action.args, "scope"),
            cooldown_seconds: readNumber(action.args, "cooldown_seconds"),
          },
        },
      }
    }
    case "delete_monitor": {
      const monitor_id = readString(action.args, "monitor_id")
      if (!monitor_id) {
        return { ok: false, problems: ["missing monitor_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_monitor", args: { monitor_id } },
      }
    }
    case "update_test_set": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_test_set",
          args: {
            test_set_id,
            display_name: readString(action.args, "display_name"),
            description: readString(action.args, "description"),
            test_set_type: readString(action.args, "test_set_type"),
            tags: readStringArray(action.args, "tags"),
          },
        },
      }
    }
    case "update_test_case": {
      const test_case_id = readString(action.args, "test_case_id")
      if (!test_case_id) {
        return { ok: false, problems: ["missing test_case_id"] }
      }
      const problems: string[] = []
      const input_str = readString(action.args, "input_str")
      const description = readStringAllowEmpty(action.args, "description")
      const expected_behaviors = readStringArray(
        action.args,
        "expected_behaviors",
      )
      // Partial patch: no SCRIPT/turns pairing here — the backend validates the
      // final stored state.
      const input_type = readInputType(action.args, problems)
      const script_turns = readScriptTurns(
        action.args,
        "script_turns",
        problems,
      )
      if (problems.length > 0) {
        return { ok: false, problems }
      }
      if (
        input_str === undefined &&
        description === undefined &&
        expected_behaviors === undefined &&
        input_type === undefined &&
        script_turns === undefined
      ) {
        return { ok: false, problems: ["missing fields to update"] }
      }
      return {
        ok: true,
        action: {
          kind: "update_test_case",
          args: {
            test_case_id,
            input_str,
            description,
            expected_behaviors,
            input_type,
            script_turns,
          },
        },
      }
    }
    case "delete_test_set": {
      const test_set_id = readString(action.args, "test_set_id")
      if (!test_set_id) {
        return { ok: false, problems: ["missing test_set_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_test_set", args: { test_set_id } },
      }
    }
    case "delete_test_case": {
      const test_case_id = readString(action.args, "test_case_id")
      if (!test_case_id) {
        return { ok: false, problems: ["missing test_case_id"] }
      }
      return {
        ok: true,
        action: { kind: "delete_test_case", args: { test_case_id } },
      }
    }
    default:
      return undefined
  }
}
